import React, { useCallback, useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";

const SortableRow = ({ faq, onEdit, onDelete, onToggle }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: faq.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            data-testid={`faq-row-${faq.id}`}
            className="grid grid-cols-[auto_1fr_auto] items-start gap-3 md:gap-4 bg-white rounded-xl ring-1 ring-stone-200 px-3 py-3 md:px-4 md:py-4"
        >
            <button
                type="button"
                {...attributes}
                {...listeners}
                className="mt-1.5 h-8 w-8 inline-flex items-center justify-center text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing"
                aria-label="Reordenar"
                data-testid={`drag-handle-${faq.id}`}
            >
                <GripVertical size={18} />
            </button>
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <h4 className="font-serif text-lg md:text-xl text-stone-900 leading-tight truncate">
                        {faq.question}
                    </h4>
                    {!faq.is_active && (
                        <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500 bg-stone-100 ring-1 ring-stone-200 rounded-full px-2 py-0.5">
                            Oculta
                        </span>
                    )}
                </div>
                <p className="mt-1 text-sm text-stone-600 line-clamp-2 whitespace-pre-wrap">
                    {faq.answer}
                </p>
            </div>
            <div className="flex items-center gap-1">
                <Switch
                    checked={faq.is_active}
                    onCheckedChange={(v) => onToggle(faq, v)}
                    data-testid={`faq-toggle-${faq.id}`}
                />
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(faq)}
                    data-testid={`faq-edit-${faq.id}`}
                    aria-label="Editar"
                >
                    <Pencil size={16} />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(faq)}
                    data-testid={`faq-delete-${faq.id}`}
                    aria-label="Eliminar"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                    <Trash2 size={16} />
                </Button>
            </div>
        </div>
    );
};

const initialEdit = {
    id: null,
    question: "",
    answer: "",
    question_en: "",
    answer_en: "",
    question_es: "",
    answer_es: "",
    is_active: true,
};

export const FaqsAdmin = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [edit, setEdit] = useState(initialEdit);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/faqs");
            setFaqs(asArray(data));
        } catch {
            toast.error("Erro a carregar FAQs.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = faqs.findIndex((f) => f.id === active.id);
        const newIndex = faqs.findIndex((f) => f.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const next = arrayMove(faqs, oldIndex, newIndex);
        setFaqs(next); // optimistic
        try {
            await api.put("/admin/faqs/reorder", { ids: next.map((f) => f.id) });
            toast.success("Ordem atualizada.");
        } catch {
            toast.error("Erro ao reordenar. A reverter.");
            load();
        }
    };

    const openNew = () => {
        setEdit(initialEdit);
        setEditOpen(true);
    };
    const openEdit = (f) => {
        setEdit({
            id: f.id,
            question: f.question || "",
            answer: f.answer || "",
            question_en: f.question_en || "",
            answer_en: f.answer_en || "",
            question_es: f.question_es || "",
            answer_es: f.answer_es || "",
            is_active: f.is_active,
        });
        setEditOpen(true);
    };

    const submitEdit = async () => {
        if (!edit.question.trim() || !edit.answer.trim()) {
            toast.error("Preencha a pergunta e a resposta em Português.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                question: edit.question.trim(),
                answer: edit.answer.trim(),
                question_en: (edit.question_en || "").trim(),
                answer_en: (edit.answer_en || "").trim(),
                question_es: (edit.question_es || "").trim(),
                answer_es: (edit.answer_es || "").trim(),
                is_active: edit.is_active,
            };
            if (edit.id) {
                const { data } = await api.patch(`/admin/faqs/${edit.id}`, payload);
                setFaqs((p) => p.map((f) => (f.id === data.id ? data : f)));
                toast.success("FAQ atualizada.");
            } else {
                const { data } = await api.post("/admin/faqs", payload);
                setFaqs((p) => [...p, data]);
                toast.success("FAQ criada.");
            }
            setEditOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erro ao guardar.");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (faq, isActive) => {
        const prev = faqs;
        setFaqs((p) => p.map((f) => (f.id === faq.id ? { ...f, is_active: isActive } : f)));
        try {
            await api.patch(`/admin/faqs/${faq.id}`, { is_active: isActive });
        } catch {
            setFaqs(prev);
            toast.error("Não foi possível alterar.");
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/admin/faqs/${confirmDelete.id}`);
            setFaqs((p) => p.filter((f) => f.id !== confirmDelete.id));
            toast.success("FAQ removida.");
        } catch {
            toast.error("Erro a remover.");
        } finally {
            setConfirmDelete(null);
        }
    };

    return (
        <div data-testid="faqs-admin">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-serif text-2xl md:text-3xl text-stone-900">FAQs</h2>
                    <p className="text-sm text-stone-500 mt-1">
                        Arraste para reordenar. Estas perguntas aparecem na landing page.
                    </p>
                </div>
                <Button
                    onClick={openNew}
                    className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50"
                    data-testid="faq-new"
                >
                    <Plus size={16} className="mr-2" /> Nova FAQ
                </Button>
            </div>

            {loading ? (
                <div className="text-center text-stone-400 py-10">A carregar…</div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={faqs.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-3" data-testid="faq-sortable-list">
                            {faqs.map((f) => (
                                <SortableRow
                                    key={f.id}
                                    faq={f}
                                    onEdit={openEdit}
                                    onDelete={(x) => setConfirmDelete(x)}
                                    onToggle={toggleActive}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {/* Edit / New dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-2xl" data-testid="faq-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>{edit.id ? "Editar FAQ" : "Nova FAQ"}</DialogTitle>
                        <DialogDescription>
                            A versão em Português é obrigatória. Inglês e Espanhol são opcionais — se ficarem vazios, a versão portuguesa é usada como fallback.
                        </DialogDescription>
                    </DialogHeader>

                    <Tabs defaultValue="pt" className="w-full">
                        <TabsList className="bg-stone-100 ring-1 ring-stone-200">
                            <TabsTrigger value="pt" data-testid="faq-tab-pt">
                                <span className="mr-1.5">🇵🇹</span> Português
                            </TabsTrigger>
                            <TabsTrigger value="en" data-testid="faq-tab-en">
                                <span className="mr-1.5">🇬🇧</span> English
                            </TabsTrigger>
                            <TabsTrigger value="es" data-testid="faq-tab-es">
                                <span className="mr-1.5">🇪🇸</span> Español
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="pt" className="space-y-4 pt-4">
                            <div>
                                <Label className="text-stone-700 text-sm">Pergunta (PT) *</Label>
                                <Input
                                    value={edit.question}
                                    onChange={(e) => setEdit({ ...edit, question: e.target.value })}
                                    className="mt-1.5 bg-stone-50 border-stone-200 h-11 rounded-lg focus-visible:ring-0 focus-visible:border-stone-900"
                                    data-testid="faq-edit-question"
                                />
                            </div>
                            <div>
                                <Label className="text-stone-700 text-sm">Resposta (PT) *</Label>
                                <Textarea
                                    value={edit.answer}
                                    onChange={(e) => setEdit({ ...edit, answer: e.target.value })}
                                    className="mt-1.5 bg-stone-50 border-stone-200 rounded-lg min-h-[140px] focus-visible:ring-0 focus-visible:border-stone-900"
                                    data-testid="faq-edit-answer"
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="en" className="space-y-4 pt-4">
                            <div>
                                <Label className="text-stone-700 text-sm">Question (EN)</Label>
                                <Input
                                    value={edit.question_en}
                                    onChange={(e) => setEdit({ ...edit, question_en: e.target.value })}
                                    className="mt-1.5 bg-stone-50 border-stone-200 h-11 rounded-lg focus-visible:ring-0 focus-visible:border-stone-900"
                                    data-testid="faq-edit-question-en"
                                    placeholder="Optional — falls back to Portuguese"
                                />
                            </div>
                            <div>
                                <Label className="text-stone-700 text-sm">Answer (EN)</Label>
                                <Textarea
                                    value={edit.answer_en}
                                    onChange={(e) => setEdit({ ...edit, answer_en: e.target.value })}
                                    className="mt-1.5 bg-stone-50 border-stone-200 rounded-lg min-h-[140px] focus-visible:ring-0 focus-visible:border-stone-900"
                                    data-testid="faq-edit-answer-en"
                                    placeholder="Optional — falls back to Portuguese"
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="es" className="space-y-4 pt-4">
                            <div>
                                <Label className="text-stone-700 text-sm">Pregunta (ES)</Label>
                                <Input
                                    value={edit.question_es}
                                    onChange={(e) => setEdit({ ...edit, question_es: e.target.value })}
                                    className="mt-1.5 bg-stone-50 border-stone-200 h-11 rounded-lg focus-visible:ring-0 focus-visible:border-stone-900"
                                    data-testid="faq-edit-question-es"
                                    placeholder="Opcional — se vacío, se usa el portugués"
                                />
                            </div>
                            <div>
                                <Label className="text-stone-700 text-sm">Respuesta (ES)</Label>
                                <Textarea
                                    value={edit.answer_es}
                                    onChange={(e) => setEdit({ ...edit, answer_es: e.target.value })}
                                    className="mt-1.5 bg-stone-50 border-stone-200 rounded-lg min-h-[140px] focus-visible:ring-0 focus-visible:border-stone-900"
                                    data-testid="faq-edit-answer-es"
                                    placeholder="Opcional — se vacío, se usa el portugués"
                                />
                            </div>
                        </TabsContent>
                    </Tabs>

                    <div className="flex items-center gap-3 pt-2 border-t border-stone-100 mt-2">
                        <Switch
                            checked={edit.is_active}
                            onCheckedChange={(v) => setEdit({ ...edit, is_active: v })}
                            data-testid="faq-edit-active"
                        />
                        <span className="text-sm text-stone-700">
                            Visível na landing page
                        </span>
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancelar</Button>
                        <Button
                            onClick={submitEdit}
                            disabled={saving}
                            className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50"
                            data-testid="faq-edit-save"
                        >
                            {saving ? "A guardar…" : (edit.id ? "Guardar" : "Criar FAQ")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="faq-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover FAQ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta ação é permanente. A pergunta "{confirmDelete?.question}" será removida.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doDelete}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="faq-delete-confirm"
                        >
                            Remover
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
